import {useCallback, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';

import {isPanelApiError, type PanelAuthPort} from '../data/PanelDataSource.js';

/**
 * Revalidação da sessão nos dois gatilhos que passavam calados.
 *
 * O guardião de `session-keeper.ts` só acorda perto do vencimento por
 * ociosidade, então até 20 minutos podiam passar entre a sessão morrer no
 * servidor e o painel avisar. Nesse intervalo, medido em produção com toda
 * requisição levando 401, trocar de aba dentro do painel mostrava o snapshot em
 * cache como se nada tivesse acontecido: a troca de rota não busca nada, porque
 * as quatro telas leem o MESMO snapshot já carregado. Recarregar a página
 * avisava, porque aí o snapshot ia à rede; quem ficava dentro do painel, não.
 *
 * Os dois gatilhos que faltavam, e que são exatamente os momentos em que a
 * pessoa volta a olhar para a tela:
 *
 * 1. troca de rota dentro do painel;
 * 2. retorno de foco ou de visibilidade da janela.
 *
 * A sonda é a mais barata que a porta tem, `GET /v1/me`, e o 401 dela cai no
 * MESMO `onSessionLost` do guardião: um aviso não modal, nunca uma navegação
 * forçada. O motivo está escrito em `AppShell.tsx`, e vale igual aqui: arrancar
 * o dono da tela desmonta o formulário com o texto que ele acabou de digitar.
 *
 * Sem porta de autenticação (modo fixture) nada disto existe: a sonda inteira
 * mora atrás de `auth`, que a fixture declara `null`. É o que mantém a suíte
 * visual e o baseline sem uma única requisição a mais.
 */

/**
 * Piso entre duas sondas.
 *
 * Sem ele, passar por três abas em sequência viraria três requisições para
 * responder à mesma pergunta. Cinco segundos é folgado o bastante para absorver
 * a rajada de cliques e curto o bastante para o aviso chegar em segundos, e não
 * em minutos, que era justamente a queixa.
 */
export const INTERVALO_MINIMO_DA_SONDA_MS = 5_000;

export const useSessionProbe = (deps: {
  auth: PanelAuthPort | null;
  onSessionLost: () => void;
  /** Injetável para teste. O aplicativo é dono do relógio. */
  now?: () => number;
  intervaloMinimoMs?: number;
}): void => {
  const {auth, onSessionLost} = deps;
  const now = deps.now ?? Date.now;
  const intervaloMinimoMs =
    deps.intervaloMinimoMs ?? INTERVALO_MINIMO_DA_SONDA_MS;
  const {pathname} = useLocation();

  // Estado da sonda em ref, e não em `useState`: nada disto pode redesenhar o
  // painel. Uma sonda que causasse render remontaria o que estiver na tela, que
  // é o efeito colateral que este reparo existe para evitar.
  const ultimaSondaEm = useRef(0);
  const sondaEmVoo = useRef(false);
  const caminhoConhecido = useRef<string | null>(null);

  const sondar = useCallback(() => {
    if (!auth || sondaEmVoo.current) {
      return;
    }
    const agora = now();
    if (agora - ultimaSondaEm.current < intervaloMinimoMs) {
      return;
    }
    // O carimbo vai ANTES da chamada: o que se limita é a taxa de saída, e
    // marcar só no retorno deixaria a rajada inteira passar enquanto a primeira
    // ainda estivesse no ar.
    ultimaSondaEm.current = agora;
    sondaEmVoo.current = true;

    void auth
      .me()
      .catch((erro: unknown) => {
        if (isPanelApiError(erro) && erro.status === 401) {
          onSessionLost();
        }
        // Qualquer outra falha (rede fora, 5xx, resposta fora do schema) NÃO é
        // sessão morta. Avisar aqui tiraria o dono do trabalho por causa de um
        // túnel que cai por três segundos, e o vencimento de verdade continua
        // coberto pelo guardião e pela próxima sonda.
      })
      .finally(() => {
        sondaEmVoo.current = false;
      });
  }, [auth, intervaloMinimoMs, now, onSessionLost]);

  useEffect(() => {
    if (!auth) {
      return;
    }
    if (caminhoConhecido.current === null) {
      // Primeira passagem com porta ligada: o snapshot que acabou de chegar já
      // provou a sessão. Sondar aqui cobraria uma requisição a mais de TODA
      // abertura do painel, para saber o que já se sabe.
      caminhoConhecido.current = pathname;
      return;
    }
    if (caminhoConhecido.current === pathname) {
      return;
    }
    caminhoConhecido.current = pathname;
    sondar();
  }, [auth, pathname, sondar]);

  useEffect(() => {
    if (!auth) {
      return;
    }

    const aoVoltarParaAJanela = () => {
      // `visibilitychange` também dispara ao ESCONDER a aba, e sondar ali seria
      // requisição para uma tela que ninguém está olhando.
      if (globalThis.document?.visibilityState === 'hidden') {
        return;
      }
      // Voltar para a janela costuma disparar `focus` e `visibilitychange`
      // juntos. As duas chegam na mesma rajada, e o piso acima transforma isso
      // em uma sonda só.
      sondar();
    };

    globalThis.addEventListener?.('focus', aoVoltarParaAJanela);
    globalThis.document?.addEventListener?.(
      'visibilitychange',
      aoVoltarParaAJanela,
    );

    return () => {
      globalThis.removeEventListener?.('focus', aoVoltarParaAJanela);
      globalThis.document?.removeEventListener?.(
        'visibilitychange',
        aoVoltarParaAJanela,
      );
    };
  }, [auth, sondar]);
};
